import type { InputFrame } from "../input";
import { railHit, surfaceAt, VERT_LAUNCH_ANGLE, wallNormal, type QuarterInfo, type Surface } from "../level";
import type { Skater } from "../skater";
import { enterAir, namePop, takePopVariant, vertOff, vertOllieVy } from "./air";
import { bail } from "./bail";
import { stepSand } from "./beach";
import { tryGrind } from "./grind";
import { hillWall, stepHillGround } from "./hill";
import { headingUpRamp, startLip } from "./lip";
import { sideTransfer } from "./side";
import { stepSkitch, trySkitch } from "./skitch";
import { startManual, stepManual } from "./manual";
import { wrapAngle } from "./math";
import type { AirState } from "./state";
import {
  AUTO_PUSH,
  BRAKE,
  CHUTE_STEER,
  CROUCH_ACCEL,
  CROUCH_SPEED,
  CRUISE,
  DROP,
  FULL_CHARGE,
  HILL_G,
  MAX_SPEED,
  MAX_VERT_LAUNCH,
  OLLIE_CHARGE_BONUS,
  OLLIE_V,
  RAMP_G,
  REVERT_LINK_WINDOW,
  REVERT_POINTS,
  ROLL_FRICTION,
  STANCE_LATE_REVERT,
  STANCE_TAKEOFF,
  STEP_UP,
  TRANSFER_OUT_CLEAR,
  TRANSFER_OUT_KEEP,
  TRANSFER_OUT_MAX,
  TRANSFER_OUT_STICK,
  TURN_RATE,
  VERT_OLLIE_LATE,
  VERT_OLLIE_MIN_ANGLE,
  VERT_POP,
  WALL_BAIL_SPEED,
  WALL_SLIDE_KEEP,
} from "./tuning";

export function stepGround(sk: Skater, input: InputFrame, dt: number): void {
  const s0 = surfaceAt(sk.level, sk.x, sk.z);
  sk.setNormal(s0);
  if (sk.skitch) {
    stepSkitch(sk, sk.skitch, input, dt);
    return;
  }
  if (sk.level.vehicles && trySkitch(sk, input)) return;
  if (sk.manualBuffer > 0 && !sk.manual) startManual(sk);

  // After a flat landing or a revert the combo waits a moment for a manual.
  if (sk.linkWindow > 0) {
    sk.linkWindow -= dt;
    if (sk.linkWindow <= 0) sk.bankCombo();
  }
  // R2: revert inside the window after a ramp landing, otherwise switch stance.
  if (sk.revertWindow > 0) {
    sk.revertWindow -= dt;
    if (input.pressed.revert) revert(sk);
    else if (sk.revertWindow <= 0) {
      sk.bankCombo();
      sk.lateRevert = STANCE_LATE_REVERT;
    }
  } else if (input.pressed.revert && !s0.quarter && sk.lateRevert <= 0) {
    // Not on a transition (R2 there is a revert, transfer or drop) and not a
    // revert pressed a little late (#134).
    sk.switchStance = !sk.switchStance;
    sk.stanceTap = STANCE_TAKEOFF;
  }
  if (input.pressed.nollie && sk.tricks.nollie) sk.nollie = !sk.nollie;

  // Steering. Right on the stick turns right regardless of fakie, like THPS.
  // On a hill (#193) the carve does the turning instead (`stepHillGround`).
  if (!sk.hill) sk.yaw -= input.moveX * TURN_RATE * dt;
  let f = sk.forward;
  let slope = s0.gx * f.x + s0.gz * f.z;

  // Gravity along the board; a hill pulls harder, so it keeps building speed.
  sk.speed += (-(sk.hill ? HILL_G : RAMP_G) * slope * dt) / Math.sqrt(1 + slope * slope);

  // Gravity across the board carves you down a bank rather than letting you
  // ride sideways across a wall forever.
  const rx = -f.z;
  const rz = f.x;
  const lat = s0.gx * rx + s0.gz * rz;
  if (Math.abs(lat) > 1e-3) {
    const aLat = (-RAMP_G * lat) / Math.sqrt(1 + s0.gx * s0.gx + s0.gz * s0.gz);
    const vx = f.x * sk.speed + rx * aLat * dt;
    const vz = f.z * sk.speed + rz * aLat * dt;
    const sign = sk.speed >= 0 ? 1 : -1;
    const target = sign > 0 ? Math.atan2(vx, vz) : Math.atan2(-vx, -vz);
    const delta = wrapAngle(target - sk.yaw);
    const maxTurn = 4 * dt;
    sk.yaw += Math.max(-maxTurn, Math.min(maxTurn, delta));
    sk.speed = sign * Math.hypot(vx, vz);
    f = sk.forward;
    slope = s0.gx * f.x + s0.gz * f.z;
  }

  // Down a water slide's chute (#163) the trough turns you round its bends,
  // whichever way along it you face.
  if (s0.guide) {
    const g = s0.guide;
    const back = g.x * f.x + g.z * f.z < 0;
    const target = back ? Math.atan2(-g.x, -g.z) : Math.atan2(g.x, g.z);
    const maxTurn = CHUTE_STEER * dt;
    sk.yaw += Math.max(-maxTurn, Math.min(maxTurn, wrapAngle(target - sk.yaw)));
    f = sk.forward;
    slope = s0.gx * f.x + s0.gz * f.z;
  }

  if (sk.hill) {
    if (stepHillGround(sk, sk.hill, input, dt)) return;
    f = sk.forward;
    slope = s0.gx * f.x + s0.gz * f.z;
  } else pushAndBrake(sk, input, slope, dt);

  if (sk.manual && stepManual(sk, sk.manual, input, dt)) return;

  // Crouch / ollie.
  if (input.held.ollie) sk.charge += dt;
  sk.crouch += ((input.held.ollie ? 1 : 0.1) - sk.crouch) * Math.min(1, dt * 14);
  if (input.released.ollie) {
    if (!onVertFace(sk, s0, slope)) {
      popOllie(sk, f, slope);
      return;
    }
    // Up the vert face the ollie waits for the lip and makes the air bigger (THPS).
    sk.vertOllie = { charge: Math.min(1, sk.charge / FULL_CHARGE), variant: takePopVariant(sk) };
  } else if (sk.vertOllie && !onVertFace(sk, s0, slope)) {
    sk.vertOllie = null;
  }
  if (!input.held.ollie) sk.charge = 0;

  // Pop onto a nearby rail from the ground (THPS lets you do this).
  if (sk.grindBuffer > 0 && tryGrind(sk, input, true)) return;

  // Move along the surface.
  const norm = Math.sqrt(1 + slope * slope);
  const hs = (sk.speed * dt) / norm;
  const nx = sk.x + f.x * hs;
  const nz = sk.z + f.z * hs;
  // Riding up a quarter pipe fast enough to step over its launch line in
  // one frame (it sits just under the coping, only centimetres from the
  // deck on a small ramp) still launches, rather than rolling onto the deck.
  const q0 = s0.quarter;
  if (q0 && sk.speed * slope > 0) {
    const uLaunch = q0.r * Math.sin(VERT_LAUNCH_ANGLE);
    if (q0.u < uLaunch && q0.u + (nx - sk.x) * q0.axisX + (nz - sk.z) * q0.axisZ >= uLaunch) {
      launchVert(sk, s0, slope, input);
      return;
    }
  }
  if (sk.outOfBounds(nx, nz)) {
    hitWallOnGround(sk, wallNormal(sk.level, nx, nz, sk.x, sk.z));
    return;
  }
  const rail = railHit(sk.level, sk.x, sk.z, nx, nz, sk.y);
  if (rail) {
    hitWallOnGround(sk, rail.n);
    return;
  }
  const expected = sk.y + slope * hs;
  const s1 = surfaceAt(sk.level, nx, nz);
  if (s1.h - expected > STEP_UP) {
    hitWallOnGround(sk, wallNormal(sk.level, nx, nz, sk.x, sk.z));
    return;
  }
  if (s1.h < expected - DROP) {
    // Rolled off an edge or the end of a kicker.
    sk.x = nx;
    sk.z = nz;
    sk.y = expected;
    enterAir(sk, (f.x * sk.speed) / norm, (slope * sk.speed) / norm, (f.z * sk.speed) / norm);
    return;
  }
  sk.x = nx;
  sk.z = nz;
  sk.y = s1.h;

  const q = s1.quarter;
  if (q) {
    const slope1 = s1.gx * f.x + s1.gz * f.z;
    const uLaunch = q.r * Math.sin(VERT_LAUNCH_ANGLE);
    if (sk.speed * slope1 > 0 && q.u >= uLaunch) launchVert(sk, s1, slope1, input);
  }
}

/**
 * Auto-push on (roughly) flat ground, in whichever direction you're rolling.
 * Crouching builds speed anywhere, which is also how you pump up to vert.
 * A fuller special meter raises every speed target (THPS).
 */
function pushAndBrake(sk: Skater, input: InputFrame, slope: number, dt: number): void {
  if (sk.level.edges && stepSand(sk, dt)) return;
  const k = sk.special.boost * sk.scale("speed");
  const sign = sk.speed >= 0 ? 1 : -1;
  let mag = Math.abs(sk.speed);
  const flat = Math.abs(slope) < 0.2;
  if (input.moveY < -0.3 && !sk.manual) {
    mag = Math.max(0, mag - BRAKE * dt);
  } else {
    if (flat && mag < CRUISE * k) mag = Math.min(CRUISE * k, mag + AUTO_PUSH * k * dt);
    if (input.held.ollie && mag < CROUCH_SPEED * k) {
      mag = Math.min(CROUCH_SPEED * k, mag + CROUCH_ACCEL * k * dt);
    }
  }
  mag = Math.max(0, mag - ROLL_FRICTION * dt);
  sk.speed = Math.min(MAX_SPEED * k, mag) * sign;
}

/** Lets go of ollie: pops into the air (a Boneless or Fastplant if the stick just asked for one). */
export function popOllie(sk: Skater, f: { x: number; z: number }, slope: number): void {
  const boost = Math.min(1, sk.charge / FULL_CHARGE) * OLLIE_CHARGE_BONUS;
  sk.charge = 0;
  const norm = Math.sqrt(1 + slope * slope);
  const variant = takePopVariant(sk);
  const pop = (OLLIE_V * (variant?.pop ?? 1) + boost) * sk.special.boost * sk.scale("ollie");
  const fakie = sk.speed < -0.5;
  const air = enterAir(sk, (f.x * sk.speed) / norm, ollieLift((slope * sk.speed) / norm, pop), (f.z * sk.speed) / norm);
  namePop(sk, air, fakie, variant);
}

/**
 * Riding up a quarter pipe's face steeper than VERT_OLLIE_MIN_ANGLE: an
 * ollie here is saved for the launch line instead of popping you off the wall.
 */
export function onVertFace(sk: Skater, s: Surface, slope: number): boolean {
  return !!s.quarter && sk.speed * slope > 0 && Math.hypot(s.gx, s.gz) >= Math.tan(VERT_OLLIE_MIN_ANGLE);
}

export function launchVert(sk: Skater, s: Surface, slope: number, input: InputFrame): void {
  const q = s.quarter!;
  const f = sk.forward;
  const ollie = sk.vertOllie;
  sk.vertOllie = null;
  const uLaunch = q.r * Math.sin(VERT_LAUNCH_ANGLE);
  // Pin to the launch line so every vert air starts from the same spot on the wall.
  sk.x += (uLaunch - q.u) * q.axisX;
  sk.z += (uLaunch - q.u) * q.axisZ;
  sk.y = surfaceAt(sk.level, sk.x, sk.z).h;
  // Grind at the coping going straight up: a lip trick instead of an air.
  if (headingUpRamp(sk, q) && (input.held.grind || sk.grindBuffer > 0)) {
    startLip(sk, q, input);
    return;
  }
  const norm = Math.sqrt(1 + slope * slope);
  let vx = (f.x * sk.speed) / norm;
  let vz = (f.z * sk.speed) / norm;
  const lift = sk.scale("air");
  const cap = MAX_VERT_LAUNCH * sk.special.boost * lift;
  let vy = Math.min(cap, Math.abs((slope * sk.speed) / norm) * VERT_POP * lift);
  if (ollie) vy = vertOllieVy(vy, ollie.charge, cap);
  const fakie = sk.speed < -0.5;
  // Kill the velocity pointing out over the deck so you come back down the
  // ramp — the classic "vert" rule from every THPS game.
  const along = vx * q.axisX + vz * q.axisZ;
  vx -= along * q.axisX;
  vz -= along * q.axisZ;
  // Off a bowl's corner (#11) the wall curves away from a carve along it,
  // so the air goes straight up and back down its line instead.
  if (q.curved) vx = vz = 0;
  const air = enterAir(sk, vx, vy, vz);
  air.vertAxis = { x: q.axisX, z: q.axisZ };
  if (q.curved) air.vertCarve = Math.abs(vertOff(sk));
  air.lipSpeed = Math.abs(sk.speed);
  air.spineLip = findSpine(sk, q);
  // Stay lined up with the wall, board against it, all the way up and back down.
  sk.setNormal(surfaceAt(sk.level, sk.x, sk.z));
  air.vertUp = { ...sk.normal };
  // Up on the stick: a transfer air, out over the deck once clear of the coping (#77).
  if (input.moveY > TRANSFER_OUT_STICK) {
    air.transferOut = {
      vh: Math.min(TRANSFER_OUT_MAX, Math.abs(sk.speed) * TRANSFER_OUT_KEEP),
      axisX: q.axisX,
      axisZ: q.axisZ,
      clearY: sk.y + q.r * Math.cos(VERT_LAUNCH_ANGLE) + TRANSFER_OUT_CLEAR,
    };
  } else if (!q.curved) {
    // Carving along the coping toward a ramp beside this one: over to it (#116).
    sideTransfer(sk, q, input, Math.abs(sk.speed));
  }
  if (ollie) namePop(sk, air, fakie, ollie.variant);
  else if (input.held.ollie) {
    // Still crouched at the lip: letting go right after still pops the ollie.
    air.vertOllieLate = { t: VERT_OLLIE_LATE, vy0: vy, cap, charge: Math.min(1, sk.charge / FULL_CHARGE), fakie };
  }
}

/**
 * A spine is a quarter pipe with another one right behind its coping,
 * facing back. Called at the vert launch point; returns the coping and the
 * far side's ramp, or null for a quarter with a deck (or a wall) behind it.
 */
export function findSpine(sk: Skater, q: QuarterInfo): AirState["spineLip"] {
  const toCoping = q.r - q.r * Math.sin(VERT_LAUNCH_ANGLE);
  const cx = sk.x + toCoping * q.axisX;
  const cz = sk.z + toCoping * q.axisZ;
  const far = surfaceAt(sk.level, cx + 0.1 * q.axisX, cz + 0.1 * q.axisZ).quarter;
  if (!far || far.axisX * q.axisX + far.axisZ * q.axisZ > -0.9) return null;
  const cy = sk.y + q.r * Math.cos(VERT_LAUNCH_ANGLE);
  return { cx, cz, cy, r: far.r, axisX: far.axisX, axisZ: far.axisZ };
}

/** Rolled into a wall (or a rail): hard and head-on bails, anything else slides along it. */
export function hitWallOnGround(sk: Skater, n: { x: number; z: number }): void {
  // On the hill (#195) any wall ends the run.
  if (hillWall(sk, sk.out)) return;
  const f = sk.forward;
  let vx = f.x * sk.speed;
  let vz = f.z * sk.speed;
  const into = -(vx * n.x + vz * n.z);
  if (into > WALL_BAIL_SPEED) {
    bail(sk, sk.out);
    sk.vx = sk.vz = 0;
    return;
  }
  if (into > 0) {
    vx += into * n.x;
    vz += into * n.z;
  }
  const along = Math.hypot(vx, vz) * WALL_SLIDE_KEEP;
  if (along < 0.5) {
    sk.speed = 0;
    return;
  }
  const sign = sk.speed >= 0 ? 1 : -1;
  sk.yaw = sign > 0 ? Math.atan2(vx, vz) : Math.atan2(-vx, -vz);
  sk.speed = sign * along;
}

/**
 * R2 after a ramp landing: pivot 180 out of fakie (or into it) and score it.
 * The combo stays open a moment for a manual (THPS 2: air, revert, manual);
 * with none it banks.
 */
export function revert(sk: Skater): void {
  sk.combo.add("Revert", REVERT_POINTS);
  sk.yaw += Math.PI;
  sk.speed = -sk.speed;
  sk.pivotT = 0;
  sk.revertWindow = 0;
  sk.linkWindow = REVERT_LINK_WINDOW;
}

/**
 * Vertical launch speed for an ollie popped while rolling at `rampVy` up (or
 * down) the surface. On flat ground it's just the pop. Going up a kicker the
 * two only partly stack: stacking them fully put a lip ollie ~3m above a 1.1m
 * kicker. Blends in over the first 2 m/s of lift so there's no seam.
 */
export function ollieLift(rampVy: number, pop: number): number {
  if (rampVy <= 0) return rampVy + pop;
  const k = Math.min(1, rampVy / 2);
  return pop * (1 - 0.2 * k) + rampVy * (1 - 0.5 * k);
}
